/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, ICoreContext } from '@msdyn365-commerce/core';
import { ProductDimensionType, ProductDimensionValueInventoryAvailability, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';

import { getDimensionAvailabilitiesForProductsAction, ProductsDimensionAvailabilitiesInput } from './get-dimension-availabilities-for-products';
import { ArrayExtensions, checkIfShouldDisplayAsSwatch, DimensionTypes, getInventoryLevelCodeFromDimensionValue,
    IDimensionsApp, InventoryLevelValues } from './utilities';

/**
 * Input class for the GetDimensionAvailabilitiesForProductSearchResult Data Action.
 */
export class DimensionAvailabilitiesForProductSearchResultInput implements IActionInput {
    public products: ProductSearchResult[];

    public dimensionContext: ICoreContext<IDimensionsApp>;

    public constructor(products: ProductSearchResult[], dimensionContext: ICoreContext<IDimensionsApp>) {
        this.products = products;
        this.dimensionContext = dimensionContext;
    }

    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => 'DimensionAvailabilitiesForProductSearchResult';

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductDimensionValueInventoryAvailability';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetDimensionAvailabilitiesForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @param dimensionContext -- The dimension app context.
 * @returns The input.
 */
export const createDimensionAvailabilitiesForProductSearchResultInput = (
    products: ProductSearchResult[],
    dimensionContext: ICoreContext<IDimensionsApp>): DimensionAvailabilitiesForProductSearchResultInput => {

    return new DimensionAvailabilitiesForProductSearchResultInput(products, dimensionContext);
};

/**
 * The Action Method for the GetDimensionAvailabilitiesForProductSearchResultInput Data Action
 * Gets the active price for product search result via the getDimensionAvailabilitiesForProduct API.
 * @param input -- The data input.
 * @param context -- The core context.
 * @returns The ProductsDimensionsAvailabilities.
 */
export async function getDimensionAvailabilitiesForProductSearchResultAction(
    input: DimensionAvailabilitiesForProductSearchResultInput,
    context: IActionContext
): Promise<IProductsDimensionsAvailabilities[][]> {
    const products = input.products;
    const productsDimensionInputs: ProductsDimensionAvailabilitiesInput[] = [];
    const inventoryLevel = input.dimensionContext.app.config.inventoryLevel;

    for (const product of products) {
        const filteredProductAttributes = product.AttributeValues?.filter(attributeValue => {
            return checkIfShouldDisplayAsSwatch(
                (attributeValue.Name?.toLocaleLowerCase() ?? '') as DimensionTypes, input.dimensionContext);
        });

        if (ArrayExtensions.hasElements(filteredProductAttributes)) {
            for (const attributeValue of filteredProductAttributes) {
                if (attributeValue.Name) {
                    const productionDimensionType: ProductDimensionType = ProductDimensionType[attributeValue.Name as keyof typeof ProductDimensionType];

                    productsDimensionInputs.push(new ProductsDimensionAvailabilitiesInput(
                        // eslint-disable-next-line inclusive-language/use-inclusive-words -- existing property.
                        product.MasterProductId ?? product.RecordId,
                        productionDimensionType));
                }
            }
        }
    }

    if (!ArrayExtensions.hasElements(productsDimensionInputs)) {
        return Promise.resolve([]);
    }

    try {
        const dimensionForProducts: ProductDimensionValueInventoryAvailability[][] = await getDimensionAvailabilitiesForProductsAction(
            productsDimensionInputs, context);
        const productsDimensionAvailabilities = productsDimensionInputs.map((dimensionInput, index) => {
            return dimensionForProducts[index].map(dimensionValuesWithInventory => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(dimensionValuesWithInventory, inventoryLevel);
                const dimensionValue = dimensionValuesWithInventory.DimensionValue!;
                return {
                    value: dimensionValue.Value ?? '',
                    isDisabled: inventoryLevelCode === InventoryLevelValues.outOfStock,
                    // eslint-disable-next-line inclusive-language/use-inclusive-words, woke/all -- existing property.
                    masterProductId: dimensionInput.masterProductId
                };
            });
        });

        if (!ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            context.telemetry.warning('ProductsDimensionAvailabilities is empty');
            return Promise.resolve([]);
        }

        return Promise.resolve(productsDimensionAvailabilities);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error.
        context.telemetry.warning(error);
    }

    return Promise.resolve([]);
}

/**
 * The GetDimensionAvailabilitiesForProductSearchResult data action.
 */
export const getDimensionAvailabilitiesForProductSearchResultDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-dimension-availabilities-for-product-search-result',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<IProductsDimensionsAvailabilities[][]>>getDimensionAvailabilitiesForProductSearchResultAction,
    input: createDimensionAvailabilitiesForProductSearchResultInput
});

export default getDimensionAvailabilitiesForProductSearchResultDataAction;
